/* ==========================================================================

   theme.js — Screenshot-matched storefront UI

   - Navigation drawer (hamburger)

   - "What's New" slider (arrows + native swipe/scroll-snap)

   - AJAX add to cart + cart count

   All interactions are event-delegated so they keep working after

   theme-editor section reloads.

   ========================================================================== */

(function () {

  'use strict';



  /* ------------------------------------------------------------------

     Navigation drawer

     ------------------------------------------------------------------ */

  function openDrawer(drawer, toggle) {

    drawer.classList.add('is-open');

    drawer.setAttribute('aria-hidden', 'false');

    if (toggle) toggle.setAttribute('aria-expanded', 'true');

    var overlay = document.querySelector('[data-vn-drawer-overlay]');

    if (overlay) overlay.classList.add('is-open');

    document.body.classList.add('vn-no-scroll');

    var closeBtn = drawer.querySelector('[data-vn-drawer-close]');

    if (closeBtn) closeBtn.focus();

  }



  function closeDrawer(drawer, toggle) {

    drawer.classList.remove('is-open');

    drawer.setAttribute('aria-hidden', 'true');

    if (toggle) toggle.setAttribute('aria-expanded', 'false');

    var overlay = document.querySelector('[data-vn-drawer-overlay]');

    if (overlay) overlay.classList.remove('is-open');

    document.body.classList.remove('vn-no-scroll');

  }



  function closeOpenDrawer() {

    var drawer = document.querySelector('[data-vn-drawer].is-open');

    if (drawer) {

      closeDrawer(drawer, document.querySelector('[data-vn-drawer-toggle]'));

    }

  }



  document.addEventListener('click', function (event) {

    var toggle = event.target.closest('[data-vn-drawer-toggle]');

    if (toggle) {

      var drawer = document.getElementById(toggle.getAttribute('aria-controls'));

      if (!drawer) return;

      if (drawer.classList.contains('is-open')) {

        closeDrawer(drawer, toggle);

      } else {

        openDrawer(drawer, toggle);

      }

      return;

    }



    if (

      event.target.closest('[data-vn-drawer-close]') ||

      event.target.matches('[data-vn-drawer-overlay]')

    ) {

      closeOpenDrawer();

    }

  });



  document.addEventListener('keydown', function (event) {

    if (event.key === 'Escape') closeOpenDrawer();

  });



  /* ------------------------------------------------------------------

     Desktop nav keyboard access — ESC closes an open mega menu,

     ←/→ move between top-level items, ↓ enters the mega panel.

     ------------------------------------------------------------------ */

  (function navKeyboard() {

    var nav = document.querySelector('.vn-nav');

    if (!nav) return;



    function topLinks() {

      return Array.prototype.slice.call(nav.querySelectorAll('.vn-nav__item > .vn-nav__link'));

    }



    nav.addEventListener('keydown', function (event) {

      var links = topLinks();

      var active = document.activeElement;

      var index = links.indexOf(active);



      if (event.key === 'Escape') {

        var openItem = nav.querySelector('.vn-nav__item:focus-within');

        if (openItem) {

          event.preventDefault();

          var link = openItem.querySelector('.vn-nav__link');

          if (link) link.focus(); // moves focus out of the panel → :focus-within closes it

          if (link) link.blur();

        }

        return;

      }



      if (index === -1) return;



      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {

        event.preventDefault();

        var next = event.key === 'ArrowRight' ? index + 1 : index - 1;

        next = (next + links.length) % links.length;

        links[next].focus();

      } else if (event.key === 'ArrowDown') {

        var item = links[index].closest('.vn-nav__item');

        var panelLink = item ? item.querySelector('.vn-mega a') : null;

        if (panelLink) {

          event.preventDefault();

          panelLink.focus();

        }

      }

    });

  })();



  /* ------------------------------------------------------------------

     Sticky header — glass/blur state once the page is scrolled

     ------------------------------------------------------------------ */

  var headerScrolled = null;



  function updateHeaderState() {

    var header = document.querySelector('.vn-header');

    if (!header) return;

    var scrolled = window.scrollY > 50;

    if (scrolled !== headerScrolled) {

      headerScrolled = scrolled;

      header.classList.toggle('vn-header--scrolled', scrolled);

    }

  }



  window.addEventListener('scroll', updateHeaderState, { passive: true });

  updateHeaderState();



  /* ------------------------------------------------------------------

     Sliders

     ------------------------------------------------------------------ */

  function initSlider(slider) {

    var track = slider.querySelector('[data-vn-slider-track]');

    var prev = slider.querySelector('[data-vn-slider-prev]');

    var next = slider.querySelector('[data-vn-slider-next]');

    if (!track) return;



    function update() {

      var max = track.scrollWidth - track.clientWidth;

      var noOverflow = max <= 1;

      slider.classList.toggle('vn-slider--no-overflow', noOverflow);

      if (prev) prev.disabled = noOverflow || track.scrollLeft <= 1;

      if (next) next.disabled = noOverflow || track.scrollLeft >= max - 1;



      /* Vertically center the arrows on the card media (not the whole card). */

      var media = track.querySelector('.vn-card__media');

      if (media) {

        slider.style.setProperty('--vn-arrow-top', media.offsetHeight / 2 + 'px');

      }

    }



    if (prev) {

      prev.addEventListener('click', function () {

        track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });

      });

    }

    if (next) {

      next.addEventListener('click', function () {

        track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });

      });

    }

    track.addEventListener('scroll', update, { passive: true });

    window.addEventListener('resize', update);

    update();

  }



  function initSliders(root) {

    (root || document).querySelectorAll('[data-vn-slider]').forEach(function (slider) {

      if (slider.dataset.vnSliderInit) return;

      slider.dataset.vnSliderInit = 'true';

      initSlider(slider);

    });

  }



  initSliders();

  document.addEventListener('shopify:section:load', function (event) {

    initSliders(event.target);

  });



  /* ------------------------------------------------------------------

     Hero slideshow (fade, dots, autoplay, swipe)

     ------------------------------------------------------------------ */

  function initSlideshows(root) {

    (root || document).querySelectorAll('[data-vn-slideshow]').forEach(function (show) {

      if (show.dataset.vnSlideshowInit) return;

      show.dataset.vnSlideshowInit = 'true';



      var slides = Array.prototype.slice.call(show.querySelectorAll('[data-vn-slide]'));

      var dots = Array.prototype.slice.call(show.querySelectorAll('[data-vn-dot]'));

      if (slides.length < 2) return;



      var index = 0;

      var timer = null;

      var interval = parseInt(show.getAttribute('data-vn-autoplay'), 10) || 0;



      function go(i) {

        index = (i + slides.length) % slides.length;

        slides.forEach(function (slide, s) {

          var active = s === index;

          slide.classList.toggle('is-active', active);

          slide.setAttribute('aria-hidden', active ? 'false' : 'true');

        });

        dots.forEach(function (dot, d) {

          var active = d === index;

          dot.classList.toggle('is-active', active);

          if (active) {

            dot.setAttribute('aria-current', 'true');

          } else {

            dot.removeAttribute('aria-current');

          }

        });

      }

      show._vnGo = go;



      function restart() {

        if (!interval) return;

        window.clearInterval(timer);

        timer = window.setInterval(function () { go(index + 1); }, interval);

      }



      dots.forEach(function (dot) {

        dot.addEventListener('click', function () {

          go(parseInt(dot.getAttribute('data-vn-dot'), 10));

          restart();

        });

      });



      if (interval) {

        restart();

        show.addEventListener('pointerenter', function () { window.clearInterval(timer); });

        show.addEventListener('pointerleave', restart);

      }



      /* Touch swipe */

      var startX = null;

      var justSwiped = false;

      show.addEventListener('touchstart', function (event) {

        startX = event.touches[0].clientX;

      }, { passive: true });

      show.addEventListener('touchend', function (event) {

        if (startX === null) return;

        var delta = event.changedTouches[0].clientX - startX;

        if (Math.abs(delta) > 40) {

          go(index + (delta < 0 ? 1 : -1));

          restart();

          /* Suppress the click that follows a swipe, so the slide link

             doesn't navigate while the user is just changing slides. */

          justSwiped = true;

          window.setTimeout(function () { justSwiped = false; }, 350);

        }

        startX = null;

      }, { passive: true });

      show.addEventListener('click', function (event) {

        if (justSwiped) {

          event.preventDefault();

          event.stopPropagation();

        }

      }, true);

    });

  }



  initSlideshows();

  document.addEventListener('shopify:section:load', function (event) {

    initSlideshows(event.target);

  });



  /* ==========================================================================

     Footer menu accordions (mobile — CSS gates the collapsed state)

     ========================================================================== */

  function initFooterAccordions(root) {

    (root || document).querySelectorAll('[data-vn-footer-toggle]').forEach(function (btn) {

      if (btn._vnFooterInit) return;

      btn._vnFooterInit = true;

      btn.addEventListener('click', function () {

        var open = btn.getAttribute('aria-expanded') === 'true';

        btn.setAttribute('aria-expanded', String(!open));

        var col = btn.closest('.vn-footer__col--menu');

        var panel = col && col.querySelector('[data-vn-footer-panel]');

        if (panel) panel.classList.toggle('is-open', !open);

      });

    });

  }



  initFooterAccordions();

  document.addEventListener('shopify:section:load', function (event) {

    initFooterAccordions(event.target);

  });



  /* Theme editor: jump to a slide when its block is selected. */

  document.addEventListener('shopify:block:select', function (event) {

    var slide = event.target.closest('[data-vn-slide]');

    if (!slide) return;

    var show = slide.closest('[data-vn-slideshow]');

    if (!show || !show._vnGo) return;

    var slides = Array.prototype.slice.call(show.querySelectorAll('[data-vn-slide]'));

    show._vnGo(slides.indexOf(slide));

  });



  /* ------------------------------------------------------------------

     Quantity selectors

     ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {

    var btn = event.target.closest('[data-vn-qty-minus], [data-vn-qty-plus]');

    if (!btn) return;

    var input = btn.parentElement.querySelector('.vn-qty__input');

    if (!input) return;

    var value = parseInt(input.value, 10) || 1;

    value += btn.hasAttribute('data-vn-qty-plus') ? 1 : -1;

    input.value = Math.max(1, value);

    input.dispatchEvent(new Event('change', { bubbles: true }));

  });



  /* ------------------------------------------------------------------

     Product gallery (thumbnails + click-to-zoom)

     ------------------------------------------------------------------ */

  function setActiveSlide(gallery, mediaId) {
    if (!gallery || !mediaId) return;
    var strId = String(mediaId);
    gallery.querySelectorAll('[data-vn-slide]').forEach(function (slide) {
      slide.classList.toggle('is-active', slide.getAttribute('data-media-id') === strId);
    });
    gallery.querySelectorAll('[data-vn-thumb]').forEach(function (thumb) {
      thumb.classList.toggle('is-active', thumb.getAttribute('data-vn-thumb') === strId);
    });

    var wrap = gallery.closest('.vn-product__gallery-wrap') || gallery.parentElement;
    if (wrap) {
      wrap.querySelectorAll('[data-pdp-dot]').forEach(function (dot) {
        dot.classList.toggle('is-active', dot.getAttribute('data-media-id') === strId);
      });
      var counter = wrap.querySelector('[data-pdp-gallery-counter]');
      if (counter) {
        var visible = Array.prototype.slice.call(gallery.querySelectorAll('[data-vn-slide]:not(.is-hidden)'));
        var idx = visible.findIndex(function (s) {
          return s.getAttribute('data-media-id') === strId;
        });
        if (idx >= 0 && visible.length > 0) {
          counter.textContent = (idx + 1) + ' / ' + visible.length;
        }
      }
      var targetSlide = gallery.querySelector('[data-vn-slide][data-media-id="' + strId + '"]');
      if (targetSlide && window.innerWidth <= 1023) {
        targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }
  }



  document.addEventListener('click', function (event) {

    var thumb = event.target.closest('[data-vn-thumb]');

    if (thumb) {

      var gallery = thumb.closest('[data-vn-gallery]');

      if (gallery) setActiveSlide(gallery, thumb.getAttribute('data-vn-thumb'));

      return;

    }



    var stage = event.target.closest('[data-vn-stage]');

    if (stage && window.matchMedia('(hover: hover) and (min-width: 1024px)').matches) {

      var img = stage.querySelector('[data-vn-slide].is-active img');

      if (!img) return;

      if (stage.classList.contains('is-zoomed')) {

        stage.classList.remove('is-zoomed');

      } else {

        var rect = stage.getBoundingClientRect();

        img.style.transformOrigin =

          ((event.clientX - rect.left) / rect.width) * 100 + '% ' +

          ((event.clientY - rect.top) / rect.height) * 100 + '%';

        stage.classList.add('is-zoomed');

      }

    }

  });



  document.addEventListener('mousemove', function (event) {

    if (!event.target.closest) return;

    var stage = event.target.closest('[data-vn-stage].is-zoomed');

    if (!stage) return;

    var img = stage.querySelector('[data-vn-slide].is-active img');

    if (!img) return;

    var rect = stage.getBoundingClientRect();

    img.style.transformOrigin =

      ((event.clientX - rect.left) / rect.width) * 100 + '% ' +

      ((event.clientY - rect.top) / rect.height) * 100 + '%';

  });



  /* ------------------------------------------------------------------

     Variant selection

     ------------------------------------------------------------------ */

  var lastVariantMediaId = 0;

  var vnRestoringVariant = false;

  var vnInitialVariantId = '';

  (function () {

    var s = document.querySelector('[data-vn-product]');

    var idEl = s && s.querySelector('[data-vn-variant-id]');

    if (idEl) vnInitialVariantId = idEl.value;

  })();



  document.addEventListener('change', function (event) {

    var input = event.target.closest('[data-vn-option-input]');

    if (!input) return;

    var scope = input.closest('[data-vn-product]');

    if (!scope) return;

    var dataEl = scope.querySelector('[data-vn-variants]');

    if (!dataEl) return;



    var variants;

    try {

      variants = JSON.parse(dataEl.textContent);

    } catch (e) {

      return;

    }



    var selected = [];

    scope.querySelectorAll('[data-vn-option-input]:checked').forEach(function (el) {

      selected[parseInt(el.getAttribute('data-position'), 10) - 1] = el.value;

      var label = scope.querySelector('[data-vn-option-value="' + el.getAttribute('data-position') + '"]');

      if (label) label.textContent = el.value;

    });



    var variant = null;

    for (var i = 0; i < variants.length; i++) {

      var match = true;

      for (var j = 0; j < variants[i].options.length; j++) {

        if (selected[j] !== undefined && variants[i].options[j] !== selected[j]) {

          match = false;

          break;

        }

      }

      if (match) {

        variant = variants[i];

        break;

      }

    }

    if (!variant) return;



    var idInput = scope.querySelector('[data-vn-variant-id]');

    if (idInput) idInput.value = variant.id;



    var priceEl = scope.querySelector('[data-vn-price-current]');

    if (priceEl) priceEl.textContent = variant.price;



    var compareEl = scope.querySelector('[data-vn-compare]');

    if (compareEl) {

      compareEl.textContent = variant.compare_at || '';

      compareEl.hidden = !variant.compare_at;

    }



    var atc = scope.querySelector('[data-vn-atc-button]');

    if (atc) {

      atc.disabled = !variant.available;

      atc.textContent = variant.available

        ? atc.getAttribute('data-label-add')

        : atc.getAttribute('data-label-soldout');

    }



    var payment = scope.querySelector('.vn-product-form__payment');

    if (payment) payment.hidden = !variant.available;



    // Only switch the stage when the variant's image actually changed —

    // sizes of one colour share the same featured image, so a size change

    // leaves the gallery exactly where the customer was looking.

    if (variant.media_id && variant.media_id !== lastVariantMediaId) {

      lastVariantMediaId = variant.media_id;

      var gallery = scope.querySelector('[data-vn-gallery]');

      if (gallery) setActiveSlide(gallery, variant.media_id);

    }



    if (window.history && window.history.pushState && !vnRestoringVariant) {

      var pageUrl = new URL(window.location.href);

      pageUrl.searchParams.set('variant', variant.id);

      // Push (not replace) so Back/Forward walks the customer's own

      // colour selections; skip when the URL already points here.

      if (pageUrl.toString() !== window.location.href) {

        window.history.pushState({ vnVariant: variant.id }, '', pageUrl.toString());

      }

    }

  });



  /* Back/Forward restores the variant from the URL (?variant=<id>); with no

     variant param it falls back to the variant the page loaded with.

     The change event drives price/SKU/stock/gallery via the listeners

     above; the guard stops that refresh from pushing a new history entry. */

  window.addEventListener('popstate', function () {

    var scope = document.querySelector('[data-vn-product]');

    if (!scope) return;

    var dataEl = scope.querySelector('[data-vn-variants]');

    if (!dataEl) return;

    var match = window.location.search.match(/[?&]variant=(\d+)/);

    var variantId = match ? parseInt(match[1], 10) : parseInt(vnInitialVariantId, 10);

    if (!variantId) return;



    var variants;

    try {

      variants = JSON.parse(dataEl.textContent);

    } catch (e) {

      return;

    }

    var variant = null;

    for (var i = 0; i < variants.length; i++) {

      if (variants[i].id === variantId) {

        variant = variants[i];

        break;

      }

    }

    if (!variant) return;



    scope.querySelectorAll('[data-vn-option-input]').forEach(function (input) {

      var pos = parseInt(input.getAttribute('data-position'), 10) - 1;

      input.checked = variant.options[pos] !== undefined && variant.options[pos] === input.value;

    });



    vnRestoringVariant = true;

    var checked = scope.querySelector('[data-vn-option-input]:checked');

    if (checked) checked.dispatchEvent(new Event('change', { bubbles: true }));

    vnRestoringVariant = false;

  });



  /* ------------------------------------------------------------------

     Copy product link

     ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {

    var copy = event.target.closest('[data-vn-copy-link]');

    if (!copy) return;

    var link = copy.getAttribute('data-vn-copy-link') || window.location.href;

    var label = copy.textContent;

    function done() {

      copy.textContent = 'Copied';

      window.setTimeout(function () { copy.textContent = label; }, 1500);

    }

    if (navigator.clipboard && navigator.clipboard.writeText) {

      navigator.clipboard.writeText(link).then(done).catch(done);

    } else {

      done();

    }

  });



  /* ------------------------------------------------------------------

     Recently viewed products (localStorage)

     ------------------------------------------------------------------ */

  (function recentlyViewed() {

    var KEY = 'vn-recently-viewed';

    var currentEl = document.querySelector('[data-vn-current-product]');

    var items = [];

    try {

      items = JSON.parse(window.localStorage.getItem(KEY)) || [];

    } catch (e) {

      items = [];

    }



    var current = null;

    if (currentEl) {

      try {

        current = JSON.parse(currentEl.textContent);

      } catch (e) {

        current = null;

      }

    }



    if (current && current.handle) {

      items = items.filter(function (item) { return item.handle !== current.handle; });

      items.unshift(current);

      items = items.slice(0, 8);

      try {

        window.localStorage.setItem(KEY, JSON.stringify(items));

      } catch (e) { /* storage unavailable */ }

    }



    var host = document.querySelector('[data-vn-recently-viewed]');

    if (!host) return;

    var grid = host.querySelector('[data-vn-recently-grid]');

    if (!grid) return;



    var others = current

      ? items.filter(function (item) { return item.handle !== current.handle; })

      : items;



    if (!others.length) {

      host.hidden = true;

      return;

    }



    others.slice(0, 4).forEach(function (item) {

      var link = document.createElement('a');

      link.className = 'vn-recent-card';

      link.href = item.url;

      var media = '';

      if (item.image) {

        media = '<span class="vn-recent-card__media"><img src="' + item.image + '" alt="" width="300" height="375" loading="lazy"></span>';

      }

      link.innerHTML = media + '<span class="vn-recent-card__title"></span><span class="vn-recent-card__price"></span>';

      link.querySelector('.vn-recent-card__title').textContent = item.title;

      link.querySelector('.vn-recent-card__price').textContent = item.price;

      grid.appendChild(link);

    });

  })();



  /* ------------------------------------------------------------------

     Wishlist — localStorage-backed toggle (delegated; no markup JS)

     Buttons carry the product handle: data-vn-wishlist="{{ product.handle }}"

     ------------------------------------------------------------------ */

  (function wishlist() {

    var KEY = 'vn-wishlist';



    function read() {

      try {

        return JSON.parse(window.localStorage.getItem(KEY)) || [];

      } catch (e) {

        return [];

      }

    }



    function write(items) {

      try {

        window.localStorage.setItem(KEY, JSON.stringify(items));

      } catch (e) { /* storage unavailable */ }

    }



    function sync() {

      var items = read();

      document.querySelectorAll('[data-vn-wishlist]').forEach(function (btn) {

        var active = items.indexOf(btn.getAttribute('data-vn-wishlist')) !== -1;

        btn.classList.toggle('is-active', active);

        btn.setAttribute('aria-pressed', active ? 'true' : 'false');

      });

    }



    document.addEventListener('click', function (event) {

      var btn = event.target.closest('[data-vn-wishlist]');

      if (!btn) return;

      event.preventDefault();

      var handle = btn.getAttribute('data-vn-wishlist');

      if (!handle) return;

      var items = read();

      var index = items.indexOf(handle);

      if (index === -1) {

        items.push(handle);

      } else {

        items.splice(index, 1);

      }

      write(items);

      sync();

    });



    sync();

  })();



  /* ------------------------------------------------------------------

     Wishlist fly-to-navbar animation

     Registered after the wishlist toggle above: delegated listeners on

     document fire in registration order, so [data-vn-wishlist] already

     carries its new state when this runs. Purely visual — the wishlist

     logic itself is untouched.

     ------------------------------------------------------------------ */

  (function wishlistFly() {

    var KEY = 'vn-wishlist';

    var DURATION = 800; // ms — inside the 700–900ms window

    var HEART_PATH = 'M19.8 4.6a5.4 5.4 0 0 0-7.6 0l-.2.2-.2-.2a5.4 5.4 0 0 0-7.6 7.6l7.6 7.7a.3.3 0 0 0 .4 0l7.6-7.7a5.4 5.4 0 0 0 0-7.6z';

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');



    /* cubic-bezier(.22,.61,.36,1) solved numerically (Newton-Raphson with

       a binary-subdivision fallback) so the rAF loop can sample eased

       progress for any linear t. */

    function cubicBezier(p1x, p1y, p2x, p2y) {

      var cx = 3 * p1x;

      var bx = 3 * (p2x - p1x) - cx;

      var ax = 1 - cx - bx;

      var cy = 3 * p1y;

      var by = 3 * (p2y - p1y) - cy;

      var ay = 1 - cy - by;



      function sampleX(t) { return ((ax * t + bx) * t + cx) * t; }

      function sampleY(t) { return ((ay * t + by) * t + cy) * t; }

      function sampleDerivX(t) { return (3 * ax * t + 2 * bx) * t + cx; }



      function solveX(x) {

        var t = x;

        var i;

        var err;

        var deriv;

        for (i = 0; i < 8; i++) {

          err = sampleX(t) - x;

          if (Math.abs(err) < 1e-6) return t;

          deriv = sampleDerivX(t);

          if (Math.abs(deriv) < 1e-6) break;

          t -= err / deriv;

          if (t < 0) t = 0;

          if (t > 1) t = 1;

        }

        var lo = 0;

        var hi = 1;

        t = x;

        for (i = 0; i < 20; i++) {

          err = sampleX(t) - x;

          if (Math.abs(err) < 1e-6) break;

          if (err > 0) hi = t; else lo = t;

          t = (lo + hi) / 2;

        }

        return t;

      }



      return function (x) {

        if (x <= 0) return 0;

        if (x >= 1) return 1;

        return sampleY(solveX(x));

      };

    }



    var ease = cubicBezier(0.22, 0.61, 0.36, 1);



    function wishlistCount() {

      try {

        return (JSON.parse(window.localStorage.getItem(KEY)) || []).length;

      } catch (e) {

        return 0;

      }

    }



    function navIcon() {

      var el = document.querySelector('.vn-header__wishlist');

      if (!el) return null;

      var rect = el.getBoundingClientRect();

      if (!rect.width || !rect.height) return null; // hidden on this viewport

      return el;

    }



    function retrigger(el, className) {

      el.classList.remove(className);

      void el.offsetWidth; // one-time reflow to restart the CSS animation

      el.classList.add(className);

    }



    function updateBadge(pop) {

      var badge = document.querySelector('[data-vn-wishlist-count]');

      if (!badge) return;

      var n = wishlistCount();

      badge.textContent = n;

      badge.classList.toggle('is-hidden', n === 0);

      if (pop && !reducedMotion.matches) retrigger(badge, 'is-pop');

    }



    function bounceNavIcon() {

      var el = navIcon();

      if (el && !reducedMotion.matches) retrigger(el, 'is-bounce');

    }



    /* Control point for a quadratic bezier between start and end, nudged

       perpendicular to the direct line — a slight arc, not a straight path. */

    function controlPoint(start, end) {

      var dx = end.x - start.x;

      var dy = end.y - start.y;

      var dist = Math.sqrt(dx * dx + dy * dy) || 1;

      var bend = Math.min(dist * 0.25, 120);

      return {

        x: (start.x + end.x) / 2 - (dy / dist) * bend,

        y: (start.y + end.y) / 2 + (dx / dist) * bend - bend * 0.4

      };

    }



    function quadAt(start, ctrl, end, t) {

      var u = 1 - t;

      return {

        x: u * u * start.x + 2 * u * t * ctrl.x + t * t * end.x,

        y: u * u * start.y + 2 * u * t * ctrl.y + t * t * end.y

      };

    }



    function createFloatingHeart() {

      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

      svg.setAttribute('viewBox', '0 0 24 24');

      svg.setAttribute('aria-hidden', 'true');

      svg.classList.add('vn-fly-heart');

      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

      path.setAttribute('d', HEART_PATH);

      svg.appendChild(path);

      return svg;

    }



    function flyToNavbar(fromEl, done) {

      var target = navIcon();

      if (!target) {

        done();

        return;

      }

      var from = fromEl.getBoundingClientRect();

      var to = target.getBoundingClientRect();

      var heart = createFloatingHeart();

      document.body.appendChild(heart);

      var w = heart.offsetWidth || 20;

      var h = heart.offsetHeight || 20;

      var start = { x: from.left + from.width / 2 - w / 2, y: from.top + from.height / 2 - h / 2 };

      var end = { x: to.left + to.width / 2 - w / 2, y: to.top + to.height / 2 - h / 2 };

      var ctrl = controlPoint(start, end);

      var startTime = null;



      // Park the heart at its origin before the first frame paints.

      heart.style.transform = 'translate3d(' + start.x + 'px,' + start.y + 'px,0)';

      heart.style.opacity = '1';



      function frame(now) {

        if (startTime === null) startTime = now;

        var linear = Math.min((now - startTime) / DURATION, 1);

        var t = ease(linear);

        var pos = quadAt(start, ctrl, end, t);

        var scale = 1 - 0.4 * t; // 1 → .6

        heart.style.transform = 'translate3d(' + pos.x + 'px,' + pos.y + 'px,0) scale(' + scale + ')';

        heart.style.opacity = String(1 - t); // 100% → 0%

        if (linear < 1) {

          window.requestAnimationFrame(frame);

        } else {

          heart.remove();

          done();

        }

      }

      window.requestAnimationFrame(frame);

    }



    document.addEventListener('click', function (event) {

      var btn = event.target.closest('[data-vn-wishlist]');

      if (!btn) return;

      // The toggle listener above has already run; is-active is the new state.

      if (!btn.classList.contains('is-active')) {

        updateBadge(false); // removal — refresh the count only

        return;

      }

      if (reducedMotion.matches) {

        updateBadge(false); // reduced motion — no flight, no pop

        return;

      }

      flyToNavbar(btn, function () {

        bounceNavIcon();

        updateBadge(true);

      });

    });



    updateBadge(false); // render the stored count on page load

  })();



  /* ------------------------------------------------------------------

     Search drawer + predictive search

     ------------------------------------------------------------------ */

  var searchDrawer = document.querySelector('[data-vn-search-drawer]');



  function openSearchDrawer() {

    if (!searchDrawer) return;

    searchDrawer.classList.add('is-open');

    searchDrawer.setAttribute('aria-hidden', 'false');

    var toggle = document.querySelector('[data-vn-search-toggle]');

    if (toggle) toggle.setAttribute('aria-expanded', 'true');

    document.body.classList.add('vn-no-scroll');

    var input = searchDrawer.querySelector('[data-vn-predictive-input]');

    if (input) input.focus();

  }



  function closeSearchDrawer() {

    if (!searchDrawer) return;

    searchDrawer.classList.remove('is-open');

    searchDrawer.setAttribute('aria-hidden', 'true');

    var toggle = document.querySelector('[data-vn-search-toggle]');

    if (toggle) toggle.setAttribute('aria-expanded', 'false');

    document.body.classList.remove('vn-no-scroll');

  }



  var predictiveInput = document.querySelector('[data-vn-predictive-input]');

  var predictiveResults = document.querySelector('[data-vn-predictive-results]');

  var searchTimer = null;



  function formatSuggestPrice(price) {

    var format = (predictiveResults && predictiveResults.getAttribute('data-money-format')) || '{{amount}}';

    var amount = parseFloat(price).toFixed(2);

    return format.replace(/\{\{\s*amount\s*\}\}/, amount);

  }



  function runPredictiveSearch(query) {

    if (!predictiveResults) return;

    if (!query || query.length < 2) {

      predictiveResults.hidden = true;

      predictiveResults.innerHTML = '';

      return;

    }

    fetch(shopRoot() + 'search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6')

      .then(function (response) { return response.json(); })

      .then(function (data) {

        var products = (data.resources && data.resources.results && data.resources.results.products) || [];

        predictiveResults.innerHTML = '';

        if (!products.length) {

          predictiveResults.innerHTML = '<p class="vn-search-drawer__empty">No results found.</p>';

          predictiveResults.hidden = false;

          return;

        }

        var grid = document.createElement('div');

        grid.className = 'vn-search-drawer__grid';

        products.forEach(function (product) {

          var link = document.createElement('a');

          link.className = 'vn-predictive-item';

          link.href = product.url;

          var image = product.image

            ? '<img src="' + product.image + '" alt="" width="56" height="70" loading="lazy">'

            : '';

          link.innerHTML =

            '<span class="vn-predictive-item__media">' + image + '</span>' +

            '<span class="vn-predictive-item__title"></span>' +

            '<span class="vn-predictive-item__price"></span>';

          link.querySelector('.vn-predictive-item__title').textContent = product.title;

          link.querySelector('.vn-predictive-item__price').textContent = formatSuggestPrice(product.price);

          grid.appendChild(link);

        });

        predictiveResults.appendChild(grid);

        var viewAll = document.createElement('a');

        viewAll.className = 'vn-search-drawer__all';

        viewAll.href = predictiveResults.getAttribute('data-search-url') + '?q=' + encodeURIComponent(query);

        viewAll.textContent = 'View all results';

        predictiveResults.appendChild(viewAll);

        predictiveResults.hidden = false;

      })

      .catch(function () {

        predictiveResults.hidden = true;

      });

  }



  if (predictiveInput) {

    predictiveInput.addEventListener('input', function () {

      window.clearTimeout(searchTimer);

      var query = predictiveInput.value.trim();

      searchTimer = window.setTimeout(function () { runPredictiveSearch(query); }, 300);

    });

  }



  /* ------------------------------------------------------------------

     Cart drawer (content via Section Rendering API)

     ------------------------------------------------------------------ */

  var cartDrawer = document.querySelector('[data-vn-cart-drawer]');

  var cartDrawerContent = document.querySelector('[data-vn-cart-drawer-content]');

  var cartDrawerOverlay = document.querySelector('[data-vn-cart-drawer-overlay]');



  function loadCartDrawer() {

    if (!cartDrawerContent) return;

    fetch(shopRoot() + '?section_id=cart-drawer')

      .then(function (response) { return response.text(); })

      .then(function (html) { cartDrawerContent.innerHTML = html; })

      .catch(function () { /* keep current content */ });

  }



  function openCartDrawer() {

    if (!cartDrawer) return;

    loadCartDrawer();

    cartDrawer.classList.add('is-open');

    cartDrawer.setAttribute('aria-hidden', 'false');

    if (cartDrawerOverlay) cartDrawerOverlay.classList.add('is-open');

    document.body.classList.add('vn-no-scroll');

  }



  function closeCartDrawer() {

    if (!cartDrawer) return;

    cartDrawer.classList.remove('is-open');

    cartDrawer.setAttribute('aria-hidden', 'true');

    if (cartDrawerOverlay) cartDrawerOverlay.classList.remove('is-open');

    document.body.classList.remove('vn-no-scroll');

  }



  document.addEventListener('click', function (event) {

    if (event.target.closest('[data-vn-search-toggle]')) {

      openSearchDrawer();

      return;

    }

    if (event.target.closest('[data-vn-search-close]')) {

      closeSearchDrawer();

      return;

    }

    if (

      event.target.closest('[data-vn-cart-drawer-close]') ||

      event.target.matches('[data-vn-cart-drawer-overlay]')

    ) {

      closeCartDrawer();

      return;

    }



    var qtyBtn = event.target.closest('[data-vn-drawer-line]');

    if (qtyBtn) {

      var line = parseInt(qtyBtn.getAttribute('data-vn-drawer-line'), 10);

      var quantity = parseInt(qtyBtn.getAttribute('data-vn-drawer-qty'), 10);

      fetch(shopRoot() + 'cart/change.js', {

        method: 'POST',

        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },

        body: JSON.stringify({ line: line, quantity: quantity })

      })

        .then(function () {

          loadCartDrawer();

          updateCartCount();

        })

        .catch(function () {});

    }

  });



  document.addEventListener('keydown', function (event) {

    if (event.key !== 'Escape') return;

    closeSearchDrawer();

    closeCartDrawer();

  });



  /* ------------------------------------------------------------------

     Cart drawer: line-key quantity stepper, remove, upsell add and

     order note save. All handlers are delegated so they keep working

     after the drawer HTML is swapped via the Section Rendering API.

     ------------------------------------------------------------------ */

  function setCartRowBusy(row, busy) {

    row.classList.toggle('is-busy', busy);

  }



  function refreshCartDrawer() {

    loadCartDrawer();

    updateCartCount();

  }



  function changeCartLineByKey(lineKey, quantity) {

    return fetch(shopRoot() + 'cart/change.js', {

      method: 'POST',

      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },

      body: JSON.stringify({ id: lineKey, quantity: quantity })

    }).then(function (response) {

      if (!response.ok) throw new Error('Cart change failed');

    });

  }



  document.addEventListener('click', function (event) {

    var stepBtn = event.target.closest('[data-vn-cart-step]');

    if (stepBtn) {

      var stepRow = stepBtn.closest('[data-vn-cart-item]');

      if (!stepRow || stepRow.classList.contains('is-busy')) return;

      var currentQty = parseInt(stepRow.getAttribute('data-vn-line-qty'), 10) || 0;

      var nextQty = Math.max(0, currentQty + parseInt(stepBtn.getAttribute('data-vn-cart-step'), 10));

      if (nextQty === currentQty) return;

      setCartRowBusy(stepRow, true);

      changeCartLineByKey(stepRow.getAttribute('data-vn-line-key'), nextQty)

        .then(refreshCartDrawer)

        .catch(function () { setCartRowBusy(stepRow, false); });

      return;

    }



    var removeBtn = event.target.closest('[data-vn-cart-remove]');

    if (removeBtn) {

      var removeRow = removeBtn.closest('[data-vn-cart-item]');

      if (!removeRow || removeRow.classList.contains('is-busy')) return;

      setCartRowBusy(removeRow, true);

      changeCartLineByKey(removeRow.getAttribute('data-vn-line-key'), 0)

        .then(refreshCartDrawer)

        .catch(function () { setCartRowBusy(removeRow, false); });

      return;

    }



    var upsellBtn = event.target.closest('[data-vn-cart-upsell-add]');

    if (upsellBtn) {

      var upsellVariant = parseInt(upsellBtn.getAttribute('data-vn-cart-upsell-add'), 10);

      if (!upsellVariant || upsellBtn.disabled) return;

      upsellBtn.disabled = true;

      fetch(shopRoot() + 'cart/add.js', {

        method: 'POST',

        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },

        body: JSON.stringify({ id: upsellVariant, quantity: 1 })

      })

        .then(function (response) {

          if (!response.ok) throw new Error('Add to cart failed');

          refreshCartDrawer();

        })

        .catch(function () { upsellBtn.disabled = false; });

    }

  });



  document.addEventListener('change', function (event) {

    var qtyInput = event.target.closest('[data-vn-cart-qty-input]');

    if (qtyInput) {

      var qtyRow = qtyInput.closest('[data-vn-cart-item]');

      if (!qtyRow || qtyRow.classList.contains('is-busy')) return;

      var inputQty = parseInt(qtyInput.value, 10);

      if (isNaN(inputQty)) inputQty = 0;

      inputQty = Math.max(0, Math.min(99, inputQty));

      setCartRowBusy(qtyRow, true);

      changeCartLineByKey(qtyRow.getAttribute('data-vn-line-key'), inputQty)

        .then(refreshCartDrawer)

        .catch(function () {

          /* Re-render so the input shows the last known quantity again. */

          setCartRowBusy(qtyRow, false);

          loadCartDrawer();

        });

      return;

    }



    var noteField = event.target.closest('[data-vn-cart-note]');

    if (noteField) {

      fetch(shopRoot() + 'cart/update.js', {

        method: 'POST',

        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },

        body: JSON.stringify({ note: noteField.value })

      }).catch(function () { /* note remains unsaved; the field keeps its value */ });

    }

  });



  /* Cart page: apply the discount code with the Enter key as well. */

  document.addEventListener('keydown', function (event) {

    if (event.key !== 'Enter') return;

    var discountField = event.target.closest ? event.target.closest('[data-vn-discount-input]') : null;

    if (!discountField) return;

    event.preventDefault();

    var applyDiscount = document.querySelector('[data-vn-discount-apply]');

    if (applyDiscount) applyDiscount.click();

  });



  /* ------------------------------------------------------------------

     Collection filters + sorting — AJAX via the Section Rendering API.

     Instant updates (no page reload), URL params maintained, browser

     back/forward supported, deep links work natively. Without JS the

     GET forms submit normally (native Shopify filtering).

     ------------------------------------------------------------------ */

  var collectionSection = document.querySelector('[data-vn-collection-section]');



  function filterFormUrl(form) {

    var params = new URLSearchParams();

    new FormData(form).forEach(function (value, key) {

      if (value !== '') params.append(key, value);

    });

    var qs = params.toString();

    return form.getAttribute('action') + (qs ? '?' + qs : '');

  }



  /* Mobile only: after a pagination change the viewport must land with the

     top of the results fully below the sticky header. This runs AFTER the

     swapped DOM has painted (double rAF) and re-checks once content settles,

     because mobile browsers can shift the scroll position after the first

     jump (scroll anchoring, late fonts/images). The re-check is cancelled as

     soon as the user touches/wheels, so it never fights a manual scroll. */

  function scrollCollectionToGrid() {

    if (window.innerWidth > 767) {

      window.requestAnimationFrame(function () {

        window.requestAnimationFrame(function () {

          window.scrollTo(0, 0);

        });

      });

      window.setTimeout(function () {

        window.scrollTo(0, 0);

      }, 100);

      return;

    }

    var target =

      collectionSection.querySelector('.vn-collection-results') ||

      collectionSection.querySelector('.vn-collection-grid') ||

      collectionSection;

    var headerEl = document.querySelector('.vn-header');

    var stickyEl = headerEl ? headerEl.closest('.shopify-section') || headerEl : null;

    var headerH = 0;

    if (stickyEl) {

      var pos = window.getComputedStyle(stickyEl).position;

      if (pos === 'sticky' || pos === 'fixed') {

        headerH = stickyEl.getBoundingClientRect().height;

      }

    }

    function alignToGrid() {

      var y =

        target.getBoundingClientRect().top +

        (window.pageYOffset || document.documentElement.scrollTop) -

        headerH -

        8;

      window.scrollTo(0, Math.max(0, y));

    }

    /* Cancel the settle re-check only on real scroll gestures. A tap on a

       pagination link fires touchstart (not touchmove), so touchstart must

       NOT cancel — otherwise the re-check never runs on mobile. */

    var userScrolled = false;

    function onUserScroll() {

      userScrolled = true;

    }

    window.addEventListener('touchmove', onUserScroll, { once: true, passive: true });

    window.addEventListener('wheel', onUserScroll, { once: true, passive: true });

    window.requestAnimationFrame(function () {

      window.requestAnimationFrame(alignToGrid);

    });

    window.setTimeout(function () {

      if (!userScrolled) alignToGrid();

    }, 350);

  }



  function loadCollection(url, push, scrollToGrid) {

    if (!collectionSection) return;

    var fetchUrl = new URL(url, window.location.origin);

    fetchUrl.searchParams.set('section_id', collectionSection.getAttribute('data-vn-collection-section'));

    collectionSection.classList.add('is-loading');

    fetch(fetchUrl.toString())

      .then(function (response) {

        return response.text();

      })

      .then(function (html) {

        var doc = new DOMParser().parseFromString(html, 'text/html');

        var fresh = doc.querySelector('[data-vn-collection-section]');

        if (!fresh) {

          window.location.href = url;

          return;

        }

        collectionSection.innerHTML = fresh.innerHTML;

        collectionSection.classList.remove('is-loading');

        if (push !== false && window.history && window.history.pushState) {

          window.history.pushState({ vnCollection: true }, '', url);

        }

        if (scrollToGrid) scrollCollectionToGrid();

        initFilterDrawer();

        initPriceSliders();

        initFilterDetails();

        initFilterCounts();

      })

      .catch(function () {

        collectionSection.classList.remove('is-loading');

        window.location.href = url; // hard fallback to native navigation

      });

  }



  if (collectionSection) {

    // Filter / sort form changes

    document.addEventListener('change', function (event) {

      var form = event.target.closest('form[data-vn-autosubmit]');

      if (!form || !collectionSection.contains(form)) return;

      // On mobile, filters are applied via the footer Apply button.

      if (form.hasAttribute('data-vn-mobile-manual') && window.innerWidth < 1024) return;

      loadCollection(filterFormUrl(form));

    });



    // Apply / Clear All / Reset / chips / pagination

    document.addEventListener('click', function (event) {

      var applyBtn = event.target.closest('.vn-filters__apply');

      if (applyBtn && collectionSection.contains(applyBtn)) {

        event.preventDefault();

        var filters = applyBtn.closest('[data-vn-filters]');

        var form = filters && filters.querySelector('[data-vn-filters-form]');

        if (form) {

          loadCollection(filterFormUrl(form));

          if (filters) filters.classList.remove('is-open');

          document.body.classList.remove('vn-no-scroll');

        }

        return;

      }



      // Pagination: same AJAX swap, plus a mobile scroll reset to the grid.

      var pageLink = event.target.closest('.vn-pagination a');

      if (pageLink && collectionSection.contains(pageLink)) {

        var pageHref = pageLink.getAttribute('href');

        if (!pageHref) return;

        event.preventDefault();

        // Don't let the browser restore a stale scroll position afterwards.

        if ('scrollRestoration' in window.history) {

          window.history.scrollRestoration = 'manual';

        }

        loadCollection(pageHref, true, true);

        return;

      }



      var link = event.target.closest(

        '.vn-filter__reset, .vn-filters__clear-all, [data-vn-filter-chip], [data-vn-filter-clear], .vn-filter__tag'

      );

      if (!link || !collectionSection.contains(link)) return;

      var href = link.getAttribute('href');

      if (!href) return;

      event.preventDefault();

      loadCollection(href);

    });



    // Browser back / forward

    window.addEventListener('popstate', function () {

      loadCollection(window.location.href, false, true);

    });



    /* Full page loads of a paginated URL (reload, direct link, or the native

       fallback above): the browser restores/keeps a stale scroll position and

       the sticky header ends up covering the grid. On mobile, take over scroll

       restoration and land at the grid top, just below the header. */

    var initialPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10);

    if (initialPage > 1 && window.innerWidth <= 767) {

      if ('scrollRestoration' in window.history) {

        window.history.scrollRestoration = 'manual';

      }

      scrollCollectionToGrid();

    }

  } else {

    // Non-collection pages: keep the plain auto-submit behaviour.

    document.addEventListener('change', function (event) {

      var form = event.target.closest('form[data-vn-autosubmit]');

      if (!form) return;

      if (form.hasAttribute('data-vn-mobile-manual') && window.innerWidth < 1024) return;

      form.submit();

    });

  }



  /* ------------------------------------------------------------------

     Collection filter drawer (mobile, full-height slide-in)

     ------------------------------------------------------------------ */

  function initFilterDrawer() {

    var filters = document.querySelector('[data-vn-filters]');

    if (!filters) return;



    var toggleBtn = filters.querySelector('[data-vn-filters-toggle]');

    var overlay = filters.querySelector('[data-vn-filters-overlay]');

    var closeBtns = filters.querySelectorAll('[data-vn-filters-close]');

    var form = filters.querySelector('[data-vn-filters-form]');



    function setMobileManual() {

      if (!form) return;

      if (window.innerWidth < 1024) {

        form.setAttribute('data-vn-mobile-manual', '');

      } else {

        form.removeAttribute('data-vn-mobile-manual');

      }

    }



    function openDrawer() {

      filters.classList.add('is-open');

      document.body.classList.add('vn-no-scroll');

      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');

    }



    function closeDrawer() {

      filters.classList.remove('is-open');

      document.body.classList.remove('vn-no-scroll');

      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');

    }



    if (toggleBtn) {

      toggleBtn.addEventListener('click', function (event) {

        event.preventDefault();

        if (filters.classList.contains('is-open')) {

          closeDrawer();

        } else {

          openDrawer();

        }

      });

    }



    if (overlay) {

      overlay.addEventListener('click', closeDrawer);

    }



    closeBtns.forEach(function (btn) {

      btn.addEventListener('click', function (event) {

        event.preventDefault();

        closeDrawer();

      });

    });



    window.addEventListener('resize', function () {

      setMobileManual();

      if (window.innerWidth >= 1024 && filters.classList.contains('is-open')) {

        closeDrawer();

      }

    });



    setMobileManual();

  }



  initFilterDrawer();



  /* ------------------------------------------------------------------

     Collapsible filter sections — smooth height animation.

     <details> stays functional without JS.

     ------------------------------------------------------------------ */

  function initFilterDetails() {

    document.querySelectorAll('[data-vn-filter]').forEach(function (details) {

      var summary = details.querySelector('summary');

      var content = details.querySelector('.vn-filter__values');

      if (!summary || !content) return;



      summary.addEventListener('click', function (event) {

        if (event.target.closest('a')) return; // Reset links navigate, never toggle

        event.preventDefault();



        function finish(targetOpen) {

          details.open = targetOpen;

          content.style.height = '';

          content.style.opacity = '';

        }



        function animateTo(targetHeight, targetOpacity, targetOpen) {

          var done = false;

          function complete() {

            if (done) return;

            done = true;

            finish(targetOpen);

          }

          content.addEventListener('transitionend', function handler() {

            content.removeEventListener('transitionend', handler);

            complete();

          });

          setTimeout(complete, 400); // fallback if no transition fires (headless, reduced motion)

          void content.offsetHeight; // register the starting style before animating

          content.style.height = targetHeight;

          content.style.opacity = targetOpacity;

        }



        if (details.open) {

          content.style.height = content.offsetHeight + 'px';

          content.style.opacity = '1';

          animateTo('0px', '0', false);

        } else {

          details.open = true;

          var target = content.offsetHeight + 'px';

          content.style.height = '0px';

          content.style.opacity = '0';

          animateTo(target, '1', true);

        }

      });

    });

  }



  initFilterDetails();



  /* ------------------------------------------------------------------

     Filter value counts — lazy-loaded per tag via the Section Rendering

     API (sections/tag-count.liquid renders just the number).

     ------------------------------------------------------------------ */

  var vnTagCountCache = {};

  var vnTagCountQueue = [];

  var vnTagCountBusy = false;



  function vnTagCountNext() {

    if (vnTagCountBusy) return;

    var job = vnTagCountQueue.shift();

    if (!job) return;

    vnTagCountBusy = true;



    fetch(job.url)

      .then(function (response) {

        return response.json();

      })

      .then(function (data) {

        var html = data[job.sectionId] || '';

        var doc = new DOMParser().parseFromString(html, 'text/html');

        var span = doc.querySelector('[data-vn-current-count]');

        var count = span ? span.getAttribute('data-vn-current-count') : null;

        vnTagCountCache[job.cacheKey] = count;

        if (count !== null) job.el.textContent = '(' + count + ')';

      })

      .catch(function () {

        /* counts are decorative — stay empty on failure */

      })

      .finally(function () {

        vnTagCountBusy = false;

        setTimeout(vnTagCountNext, 120); // gentle on Shopify's rate limits

      });

  }



  function initFilterCounts() {

    var marker = document.querySelector('[data-vn-count-section]');

    if (!marker) return;

    var sectionId = marker.getAttribute('data-vn-count-section');

    var form = document.querySelector('[data-vn-filters-form]');

    if (!form) return;

    var basePath = (form.getAttribute('action') || window.location.pathname).split('/').slice(0, 3).join('/');



    document.querySelectorAll('[data-vn-count]').forEach(function (el) {

      if (el.getAttribute('data-vn-count-loading')) return;

      el.setAttribute('data-vn-count-loading', '1');

      var tag = el.getAttribute('data-vn-count');

      var cacheKey = basePath + '/' + tag;



      if (vnTagCountCache[cacheKey] !== undefined) {

        if (vnTagCountCache[cacheKey] !== null) el.textContent = '(' + vnTagCountCache[cacheKey] + ')';

        return;

      }



      vnTagCountQueue.push({

        el: el,

        cacheKey: cacheKey,

        sectionId: sectionId,

        url: basePath + '/' + encodeURIComponent(tag) + '?sections=' + encodeURIComponent(sectionId),

      });

      vnTagCountNext();

    });

  }



  initFilterCounts();



  /* ------------------------------------------------------------------

     Collection filter price-range slider

     ------------------------------------------------------------------ */

  function initPriceSliders() {

    document.querySelectorAll('[data-vn-price-filter]').forEach(function (wrap) {

      var rangeBar = wrap.querySelector('[data-vn-price-range]');

      var minHandle = wrap.querySelector('[data-vn-price-min]');

      var maxHandle = wrap.querySelector('[data-vn-price-max]');

      var minInput = wrap.querySelector('[data-vn-price-input-min]');

      var maxInput = wrap.querySelector('[data-vn-price-input-max]');

      var readout = wrap.querySelector('[data-vn-price-readout]');

      var symbol = readout ? readout.textContent.trim().charAt(0) : '';

      var max = parseFloat(wrap.getAttribute('data-range-max')) || 0;



      if (!rangeBar || !minHandle || !maxHandle || !minInput || !maxInput || max <= 0) return;



      function updateSlider() {

        var minVal = parseFloat(minHandle.value) || 0;

        var maxVal = parseFloat(maxHandle.value) || max;



        if (minVal > maxVal) {

          var temp = minVal;

          minVal = maxVal;

          maxVal = temp;

        }



        minInput.value = minVal;

        maxInput.value = maxVal;



        if (readout) readout.textContent = symbol + minVal + ' — ' + symbol + maxVal;



        var minPercent = (minVal / max) * 100;

        var maxPercent = (maxVal / max) * 100;



        rangeBar.style.left = minPercent + '%';

        rangeBar.style.width = (maxPercent - minPercent) + '%';

      }



      function updateInputs() {

        var minVal = parseFloat(minInput.value) || 0;

        var maxVal = parseFloat(maxInput.value) || max;



        if (minVal < 0) minVal = 0;

        if (maxVal > max) maxVal = max;

        if (minVal > maxVal) minVal = maxVal;



        minHandle.value = minVal;

        maxHandle.value = maxVal;

        updateSlider();

      }



      function applyPrice() {

        if (!collectionSection) return;

        var form = wrap.closest('form[data-vn-autosubmit]');

        if (!form) return;

        if (form.hasAttribute('data-vn-mobile-manual') && window.innerWidth < 1024) return;

        loadCollection(filterFormUrl(form));

      }



      minHandle.addEventListener('input', updateSlider);

      maxHandle.addEventListener('input', updateSlider);

      minHandle.addEventListener('change', function () {

        updateSlider();

        applyPrice();

      });

      maxHandle.addEventListener('change', function () {

        updateSlider();

        applyPrice();

      });

      minInput.addEventListener('change', updateInputs);

      maxInput.addEventListener('change', updateInputs);



      updateInputs();

    });

  }



  initPriceSliders();



  /* ------------------------------------------------------------------

     Cart page: discount code + shipping estimate

     ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {

    var applyBtn = event.target.closest('[data-vn-discount-apply]');

    if (applyBtn) {

      var discountInput = document.querySelector('[data-vn-discount-input]');

      var checkoutLink = document.querySelector('[data-vn-checkout]');

      if (discountInput && checkoutLink && discountInput.value.trim()) {

        var checkoutUrl = new URL(checkoutLink.getAttribute('href'), window.location.origin);

        checkoutUrl.searchParams.set('discount', discountInput.value.trim());

        checkoutLink.setAttribute('href', checkoutUrl.toString());

        applyBtn.textContent = 'Applied';

      }

      return;

    }



    var shippingBtn = event.target.closest('[data-vn-shipping-btn]');

    if (shippingBtn) {

      var wrap = shippingBtn.closest('[data-vn-shipping]');

      if (!wrap) return;

      var country = wrap.querySelector('[data-vn-shipping-country]').value;

      var zip = wrap.querySelector('[data-vn-shipping-zip]').value;

      var out = wrap.querySelector('[data-vn-shipping-results]');

      out.textContent = 'Calculating…';

      fetch(

        shopRoot() +

          'cart/shipping_rates.json?shipping_address[country]=' +

          encodeURIComponent(country) +

          '&shipping_address[zip]=' +

          encodeURIComponent(zip)

      )

        .then(function (response) { return response.json(); })

        .then(function (data) {

          if (data.shipping_rates && data.shipping_rates.length) {

            out.innerHTML = '';

            data.shipping_rates.forEach(function (rate) {

              var line = document.createElement('p');

              line.textContent = rate.name + ': ' + rate.price + ' ' + (rate.currency || '');

              out.appendChild(line);

            });

          } else {

            out.textContent = 'No shipping rates found for this address.';

          }

        })

        .catch(function () {

          out.textContent = 'Could not calculate shipping.';

        });

    }

  });



  /* ------------------------------------------------------------------

     Login: recover-password toggle. Addresses: delete confirmation.

     ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {

    var recoverToggle = event.target.closest('[data-vn-recover-toggle]');

    if (recoverToggle) {

      event.preventDefault();

      var loginCard = document.querySelector('[data-vn-login-card]');

      var recoverCard = document.querySelector('[data-vn-recover-card]');

      if (loginCard && recoverCard) {

        loginCard.hidden = !loginCard.hidden;

        recoverCard.hidden = !recoverCard.hidden;

      }

      return;

    }



    var deleteBtn = event.target.closest('[data-vn-confirm-delete]');

    if (deleteBtn && !window.confirm('Delete this address?')) {

      event.preventDefault();

    }

  });



  /* ------------------------------------------------------------------

     AJAX add to cart + cart count bubble

     ------------------------------------------------------------------ */

  function shopRoot() {

    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  }



  function updateCartCount() {

    fetch(shopRoot() + 'cart.js', { headers: { Accept: 'application/json' } })

      .then(function (response) { return response.json(); })

      .then(function (cart) {

        document.querySelectorAll('[data-vn-cart-count]').forEach(function (el) {

          el.textContent = cart.item_count;

          el.classList.toggle('is-hidden', cart.item_count === 0);

        });

      })

      .catch(function () { /* count stays as-is */ });

  }



  document.addEventListener('submit', function (event) {

    var form = event.target.closest('form[data-vn-atc]');

    if (!form) return;

    event.preventDefault();



    var button = form.querySelector('[type="submit"]');

    var label = button ? button.textContent : '';

    if (button) button.disabled = true;



    fetch(form.action, {

      method: 'POST',

      body: new FormData(form),

      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }

    })

      .then(function (response) {

        if (!response.ok) throw new Error('Add to cart failed');

        return response.json();

      })

      .then(function () {

        updateCartCount();

var headerCartButton = document.querySelector('.vn-header__cart');

if (headerCartButton) {
  window.setTimeout(function () {
    headerCartButton.click();
  }, 150);
}

        if (button) {

          button.textContent = button.getAttribute('data-added-label') || 'Added';

          window.setTimeout(function () {

            button.textContent = label;

            button.disabled = false;

          }, 1500);

        }

      })

      .catch(function () {

        /* Fall back to a regular (non-AJAX) submit. */

        form.submit();

      });

  });

})();

